import type { PlaylistValidation, ResolvedTrack, YearBreakdownEntry } from '@open-songster/shared'
import { DEFAULT_TRACKS_PER_YEAR } from '@open-songster/shared'

/** Resolved tracks grouped by year, ascending. Unresolved tracks are ignored. */
export function groupTracksByYear(tracks: readonly ResolvedTrack[]): Map<number, ResolvedTrack[]> {
  const byYear = new Map<number, ResolvedTrack[]>()
  for (const track of tracks) {
    if (track.year === null) continue
    const bucket = byYear.get(track.year)
    if (bucket) bucket.push(track)
    else byYear.set(track.year, [track])
  }
  return new Map([...byYear.entries()].sort(([a], [b]) => a - b))
}

/**
 * How many tracks a year needs to be playable.
 *
 * Normally the full `tracksPerYear`, so every year of the game is the same
 * depth. With an uneven distribution allowed, one track is enough: the year
 * contributes what it has, and the cards it cannot fill are skipped during
 * play. That trades an even game for a much wider spread of years, which is
 * the better trade for a playlist that covers fifty years thinly.
 */
export function requiredTracks(tracksPerYear: number, allowUneven = false): number {
  return allowUneven ? 1 : tracksPerYear
}

export function computeYearBreakdown(
  tracks: readonly ResolvedTrack[],
  tracksPerYear: number = DEFAULT_TRACKS_PER_YEAR,
  allowUneven = false,
): YearBreakdownEntry[] {
  const required = requiredTracks(tracksPerYear, allowUneven)
  return [...groupTracksByYear(tracks).entries()].map(([year, yearTracks]) => ({
    year,
    trackCount: yearTracks.length,
    qualifying: yearTracks.length >= required,
    // What the year will actually contribute: never more than the setting,
    // never more than it has.
    playableCount: Math.min(yearTracks.length, tracksPerYear),
  }))
}

/**
 * Cards that will be scanned with no song behind them.
 *
 * Only meaningful with an uneven distribution: it is what a player is signing
 * up to skip, and the number they deserve to see before the game starts.
 */
export function unplayableCardCount(
  breakdown: readonly YearBreakdownEntry[],
  tracksPerYear: number,
): number {
  return breakdown
    .filter((entry) => entry.qualifying)
    .reduce(
      (total, entry) => total + (tracksPerYear - Math.min(entry.trackCount, tracksPerYear)),
      0,
    )
}

/** Qualifying years that cannot fill every card, ascending. */
export function thinYears(
  breakdown: readonly YearBreakdownEntry[],
  tracksPerYear: number,
): number[] {
  return breakdown
    .filter((entry) => entry.qualifying && entry.trackCount < tracksPerYear)
    .map((entry) => entry.year)
}

/** Years with enough resolved tracks to supply the game, ascending. */
export function qualifyingYears(
  tracks: readonly ResolvedTrack[],
  tracksPerYear: number = DEFAULT_TRACKS_PER_YEAR,
  allowUneven = false,
): number[] {
  return computeYearBreakdown(tracks, tracksPerYear, allowUneven)
    .filter((entry) => entry.qualifying)
    .map((entry) => entry.year)
}

/** A playlist is selectable as soon as one year qualifies. */
export function validatePlaylist(
  tracks: readonly ResolvedTrack[],
  tracksPerYear: number = DEFAULT_TRACKS_PER_YEAR,
  allowUneven = false,
): PlaylistValidation {
  const breakdown = computeYearBreakdown(tracks, tracksPerYear, allowUneven)
  const qualifying = breakdown.filter((entry) => entry.qualifying).map((entry) => entry.year)
  return {
    valid: qualifying.length > 0,
    qualifyingYears: qualifying,
    breakdown,
    totalTrackCount: tracks.length,
    unresolvedTrackCount: tracks.filter((track) => track.year === null).length,
  }
}

/** True when the playlist has at least one qualifying year inside the range. */
export function playlistCoversYearRange(
  validation: PlaylistValidation,
  from: number,
  to: number,
): boolean {
  return validation.qualifyingYears.some((year) => year >= from && year <= to)
}

/**
 * How many years would be playable at a given setting, from a breakdown that
 * was computed at some other setting.
 *
 * `YearBreakdownEntry.qualifying` is only true for the setting it was computed
 * with, but `trackCount` is raw data — so a curator can be shown the effect of
 * a change before making it.
 */
export function playableYearCount(
  breakdown: readonly YearBreakdownEntry[],
  tracksPerYear: number,
): number {
  return breakdown.filter((entry) => entry.trackCount >= tracksPerYear).length
}
